import type {AudioTrackSwitchedData, Level, SubtitleTrackSwitchData} from 'hls.js';

import {Analytics} from '../../core/Analytics';
import {DownloadSpeedMeter} from '../../core/DownloadSpeedMeter';
import {Event} from '../../enums/Event';
import {MIMETypes} from '../../enums/MIMETypes';
import {Player} from '../../enums/Player';
import {ErrorDetailBackend} from '../../features/errordetails/ErrorDetailBackend';
import {ErrorDetailTracking} from '../../features/errordetails/ErrorDetailTracking';
import {Feature} from '../../features/Feature';
import {FeatureConfig} from '../../features/FeatureConfig';
import {CodecInfo} from '../../types/CodecInfo';
import {DownloadSpeedInfo} from '../../types/DownloadSpeedInfo';
import {FeatureConfigContainer} from '../../types/FeatureConfigContainer';
import {PlaybackInfo} from '../../types/PlaybackInfo';
import {QualityLevelInfo} from '../../types/QualityLevelInfo';
import {SubtitleInfo} from '../../types/SubtitleInfo';
import {isNumber} from '../../utils/numberUtils';
import {isNotNullish, isNullish} from '../../utils/Utils';
import {HTML5InternalAdapter} from '../html5/HTML5InternalAdapter';
import {HTMLVideoElementProxy} from '../html5/HTMLVideoElementProxy';
import {InternalAdapterAPI} from '../internal/InternalAdapterAPI';

import {HlsjsPlayerProxy} from './HlsjsPlayerProxy';
import {HLS_PLAYER_EVENT} from './player/HlsJsPlayerTypes';
import {HlsSpeedMeterAdapter} from './player/HlsSpeedMeterAdapter';

export class HlsInternalAdapter extends HTML5InternalAdapter implements InternalAdapterAPI {
  private readonly hls: HlsjsPlayerProxy;
  private speedMeter: DownloadSpeedMeter;

  constructor(hls: HlsjsPlayerProxy) {
    super(undefined);
    this.hls = hls;
    this.speedMeter = new HlsSpeedMeterAdapter(hls, new DownloadSpeedMeter()).getDownloadSpeedMeter();
  }

  override initialize(analytics: Analytics): Array<Feature<FeatureConfigContainer, FeatureConfig>> {
    super.initialize(analytics);
    this.registerHlsEvents();
    const errorDetailTracking = new ErrorDetailTracking(
      analytics.errorDetailTrackingSettingsProvider,
      new ErrorDetailBackend(analytics.errorDetailTrackingSettingsProvider.collectorConfig),
      [analytics.errorDetailSubscribable],
      undefined,
    );
    return [errorDetailTracking];
  }

  override getPlayerName = () => Player.HLSJS;

  getPlayerVersion = () => this.hls.version ?? 'unknown';

  override getCurrentPlaybackInfo(): PlaybackInfo {
    const selectedSubtitle = this.getSelectedSubtitleLanguage();

    const playbackInfo: PlaybackInfo = {
      ...super.getCurrentPlaybackInfo(),
      audioLanguage: this.getSelectedAudioLanguage(),
      subtitleEnabled: isNotNullish(selectedSubtitle) ? selectedSubtitle.enabled : undefined,
      subtitleLanguage: isNotNullish(selectedSubtitle) ? selectedSubtitle.language : undefined,
    };

    const codecInfo = getHlsCodecInfo(this.hls);
    this.sourceInfoFallbackService.applyAndStoreCodecInfo(playbackInfo, codecInfo);

    return playbackInfo;
  }
  override getDownloadSpeedInfo(): DownloadSpeedInfo {
    return this.speedMeter.getInfo();
  }
  override resetDownloadSpeedInfoMeasures() {
    this.speedMeter.reset();
  }

  getCurrentQualityLevelInfo(): QualityLevelInfo | null {
    const currentLevelObj = getHlsCurrentLevel(this.hls);
    if (!currentLevelObj) {
      return null;
    }

    const bitrate = currentLevelObj.bitrate;
    const width = currentLevelObj.width;
    const height = currentLevelObj.height;

    return {
      bitrate: bitrate,
      width: width,
      height: height,
    };
  }

  override isLive = () => {
    const currentLevelObj = getHlsCurrentLevel(this.hls);

    if (isNullish(currentLevelObj?.details)) {
      return undefined;
    }

    return currentLevelObj.details.live;
  };

  override getMIMEType() {
    return MIMETypes.HLS;
  }

  override getStreamURL(): string | undefined {
    return this.hls.url ?? undefined;
  }

  registerHlsEvents() {
    this.hls.on(HLS_PLAYER_EVENT.MEDIA_ATTACHING, this.onMediaAttaching.bind(this));
    this.hls.on(HLS_PLAYER_EVENT.MEDIA_DETACHING, this.onMediaDetaching.bind(this));
    this.hls.on(HLS_PLAYER_EVENT.MANIFEST_LOADING, this.onManifestLoading.bind(this));
    this.hls.on(HLS_PLAYER_EVENT.AUDIO_TRACK_SWITCHED, this.onAudioTrackSwitched.bind(this));
    this.hls.on(HLS_PLAYER_EVENT.SUBTITLE_TRACK_SWITCH, this.onSubtitleLanguageSwitched.bind(this));
    this.hls.on(HLS_PLAYER_EVENT.DESTROYING, this.onDestroying.bind(this));
    this.hls.on(HLS_PLAYER_EVENT.ERROR, this.onHlsError.bind(this));

    // media is already attached, event has been triggered before
    // or we are in the event handler of this event itself.
    // we can not know how the stacktrace to this constructor will look like.
    // therefore we will guard from this case in
    // the onMediaAttaching method (avoid running it twice)
    if (this.hls.media) {
      this.onMediaAttaching();
      this.onManifestLoading();
    }
  }

  onMediaAttaching() {
    // in case we are called again (when we are triggering this ourselves
    // but from the event handler of MEDIA_ATTACHING) we should not run again.
    if (this.mediaElement) {
      return;
    }

    this.mediaElement = new HTMLVideoElementProxy(this.hls.media as HTMLVideoElement);

    this.registerMediaElement();
    this.onMaybeReady();
  }

  onMediaDetaching() {
    this.unregisterMediaElement();
  }

  onManifestLoading() {
    this.onMaybeReady();
  }

  /**
   * errorData: { type : error type, details : error details, fatal : if true, hls.js cannot/will not try to recover, if false, hls.js will try to recover,other error specific data }
   * https://hls-js.netlify.com/api-docs/file/src/events.js.html#lineNumber98
   */
  onHlsError(errorName, errorData) {
    const isFatal = errorData ? errorData.fatal : true;
    if (isFatal) {
      const mediaElement = this.mediaElement;
      let currentTime;
      if (isNotNullish(mediaElement)) {
        currentTime = mediaElement.currentTime;
      }

      const errorMessage = isNotNullish(errorData) ? `${errorData.type}: ${errorData.details}` : undefined;
      this.eventCallback(Event.ERROR, {
        currentTime,
        code: this.getErrorCodeFromHlsErrorType(errorData.type),
        message: errorMessage,
        originalError: errorData,
        data: {},
      });
    }
  }

  onAudioTrackSwitched(_event: string, _data: AudioTrackSwitchedData) {
    const mediaElement = this.mediaElement;
    let currentTime;
    if (isNotNullish(mediaElement)) {
      currentTime = mediaElement.currentTime;
    }

    this.eventCallback(Event.AUDIOTRACK_CHANGED, {
      currentTime,
    });
  }

  onSubtitleLanguageSwitched(_event: string, _data: SubtitleTrackSwitchData) {
    const mediaElement = this.mediaElement;
    let currentTime;
    if (isNotNullish(mediaElement)) {
      currentTime = mediaElement.currentTime;
    }

    this.eventCallback(Event.SUBTITLE_CHANGE, {
      currentTime,
    });
  }

  private onDestroying = () => {
    this.eventCallback(Event.SOURCE_UNLOADED, {});
    this.release();
  };

  /**
   * returns mapped error code for Hlsjs ErrorTypes
   * @param type one of the ErrorTypes according to https://github.com/video-dev/hls.js/blob/v1.5.1/src/errors.ts#L1
   */
  private getErrorCodeFromHlsErrorType(type: string): number {
    switch (type) {
      case 'networkError':
        return 2;
      case 'mediaError':
        return 3;
      case 'keySystemError':
        return 4;
      case 'muxError':
        return 5;
      // default equals `otherError`
      default:
        return 1;
    }
  }

  private getSelectedAudioLanguage(): string | undefined {
    const audioTrack = this.hls.audioTrack;
    if (isNullish(audioTrack) || audioTrack < 0) {
      return undefined;
    }
    const audioTracks = this.hls.audioTracks;
    if (isNullish(audioTracks) || audioTracks.length === 0) {
      return undefined;
    }
    return audioTracks[audioTrack].lang;
  }

  private getSelectedSubtitleLanguage(): SubtitleInfo | undefined {
    // Check if function is supported (in latest hls version), otherwise use fallback option
    if (isNotNullish(this.hls.subtitleDisplay)) {
      const subtitleTrackValue = this.hls.subtitleTrack;
      const isSubtitleDisplayed =
        isNotNullish(subtitleTrackValue) && subtitleTrackValue >= 0 && this.hls.subtitleDisplay;

      const language = isSubtitleDisplayed ? this.hls.subtitleTracks?.[subtitleTrackValue]?.lang : undefined;

      return {
        enabled: isSubtitleDisplayed,
        language,
      };
    } else {
      const {subtitleTrackController} = this.hls as any;
      if (isNotNullish(subtitleTrackController) && isNotNullish(subtitleTrackController.media)) {
        return this.getSelectedSubtitleFromMediaElement(subtitleTrackController.media);
      }
    }
    return undefined;
  }
}

export const getHlsCodecInfo = (hls: HlsjsPlayerProxy): CodecInfo | undefined => {
  const currentLevelObj = getHlsCurrentLevel(hls);
  if (!currentLevelObj) {
    return undefined;
  }

  return {
    videoCodec: currentLevelObj.videoCodec,
    audioCodec: currentLevelObj.audioCodec,
  };
};

export const getHlsCurrentLevel = (hls: HlsjsPlayerProxy): Level | undefined => {
  const levels = hls.levels;
  const currentLevel = hls.currentLevel;
  if (isNullish(levels) || levels.length === 0) {
    return undefined;
  }

  if (!isNumber(currentLevel) || currentLevel < 0) {
    return undefined;
  }

  return levels[currentLevel];
};
